import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { AlertCircle, ChevronLeft, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  CoverImageDrop,
  type CoverImageResult,
} from "@/components/CoverImageDrop"
import { TagsInput } from "@/components/TagsInput"
import {
  useCreateCoach,
  useGetCoach,
  useUpdateCoach,
  type CoachWritePayload,
} from "@/lib/queries/coaches"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Coach form (combined New + Edit) — V-D
// API:    POST /admin/coach_get      { id }                    (when editing)
//         POST /admin/coach_create   { name, bio, topics, avatar, cover, display_order, is_published }
//         POST /admin/coach_update   { id, ...same fields }
//
// Slug is server-generated from name (kebab-case, deduped). Shown
// read-only to give admins visibility but not editability.
//
// `avatar` / `cover` are sent as the s3response object on new upload,
// OMITTED on edit-without-new so the backend keeps the existing image.
// ───────────────────────────────────────────────────────────────────────

// V-L Phase 2 / paid-DM tier list (matches the 5 IAP products
// registered in App Store Connect / Play Console / RevenueCat).
// `null` = paid DMs disabled for this creator.
const RATE_TIERS: { label: string; value: number | null }[] = [
  { label: "Disabled", value: null },
  { label: "$1.99", value: 199 },
  { label: "$4.99", value: 499 },
  { label: "$9.99", value: 999 },
  { label: "$24.99", value: 2499 },
  { label: "$49.99", value: 4999 },
]

const schema = z.object({
  name: z.string().min(1, "Name is required").max(120),
  // Headline shown on the CoachesFollow onboarding rail. FE truncates
  // at ~60; we allow up to 120 so drafts slightly over still save.
  tagline: z.string().trim().max(120, "Tagline must be 120 characters or fewer").default(""),
  bio: z.string().max(2000).optional().default(""),
  topics: z.array(z.string()).default([]),
  display_order: z.coerce.number().int().min(0).default(0),
  // FE-ask (Kush 2026-09-16): default the Create form to Published:true.
  // The two coach onboarding misses (Locker Room, Akeem Haynes) were both
  // saved with the toggle left OFF and vanished from the admin Creators
  // list until manually flipped. Anyone using this form intends to
  // surface the coach; backfilled author stubs (the only reason we'd
  // want unpublished coaches by default) come in via the seeder, not
  // this form. Edit-flow prefills from the doc so existing rows are
  // unaffected.
  is_published: z.boolean().default(true),
  // V-Q5: directory-listing gate, distinct from is_published. Default
  // true on the form because the only reason to add a coach via this
  // form is to surface them — backfilled author stubs (which want
  // is_published: true / isPublicCoach: false) come in via the seeder.
  isPublicCoach: z.boolean().default(true),
  verified: z.boolean().default(false),
  // V-O + V-Q12: per-coach hide toggles for the mobile "Ask" and
  // "Subscribe" CTAs. Default true on both — an old form submission
  // that omits them keeps the current behavior. Flip to false to hide
  // the CTA on this coach's cards.
  acceptsConversations: z.boolean().default(true),
  subscriptionEnabled: z.boolean().default(true),
  // Stored as cents on the backend; UI uses string "null" / "199" / etc.
  // to keep the Select primitive simple, mapped back to number|null on submit.
  message_rate_cents: z.string().default(""),
  // V-L Creator Profile redesign — public contact + social fields. All
  // optional; empty string is the "clear / don't show" signal. URLs/email
  // also validated server-side (don't rely on client validation alone).
  email: z
    .string()
    .trim()
    .max(200)
    .refine((v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), {
      message: "Enter a valid email address or leave blank",
    })
    .default(""),
  website: z
    .string()
    .trim()
    .max(500)
    .refine((v) => v === "" || /^https?:\/\//i.test(v), {
      message: "Website must start with https://",
    })
    .default(""),
  instagram: z.string().trim().max(100).default(""),
  twitter: z.string().trim().max(100).default(""),
  linkedin: z.string().trim().max(300).default(""),
  tiktok: z.string().trim().max(100).default(""),
  facebook: z.string().trim().max(300).default(""),
})

type FormValues = z.infer<typeof schema>

const DEFAULT_VALUES: FormValues = {
  name: "",
  tagline: "",
  bio: "",
  topics: [],
  display_order: 0,
  is_published: true,
  isPublicCoach: true,
  verified: false,
  acceptsConversations: true,
  subscriptionEnabled: true,
  message_rate_cents: "",
  email: "",
  website: "",
  instagram: "",
  twitter: "",
  linkedin: "",
  tiktok: "",
  facebook: "",
}

export default function CoachForm() {
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const navigate = useNavigate()

  const coachQ = useGetCoach(id)
  const createMut = useCreateCoach()
  const updateMut = useUpdateCoach()
  const submittingMut = isEdit ? updateMut : createMut

  const [avatarResult, setAvatarResult] = useState<CoverImageResult | null>(null)
  const [coverResult, setCoverResult] = useState<CoverImageResult | null>(null)
  const [isUploading, setIsUploading] = useState(0) // ref-count style
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  })

  useEffect(() => {
    if (!isEdit || !coachQ.data) return
    reset({
      name: coachQ.data.name ?? "",
      tagline: coachQ.data.tagline ?? "",
      bio: coachQ.data.bio ?? "",
      topics: Array.isArray(coachQ.data.topics) ? coachQ.data.topics : [],
      display_order:
        typeof coachQ.data.display_order === "number"
          ? coachQ.data.display_order
          : 0,
      is_published: !!coachQ.data.is_published,
      // V-Q5: existing coaches missing the field default to false in the
      // form (matches schema default); the backfill seeder handles the
      // grandfather pass for legacy admin-curated rows.
      isPublicCoach: !!coachQ.data.isPublicCoach,
      verified: !!coachQ.data.verified,
      // Both default true — if a legacy coach doc lacks the field the
      // API omits it, so we treat "missing" as "on" to match backend.
      acceptsConversations: coachQ.data.acceptsConversations !== false,
      subscriptionEnabled: coachQ.data.subscriptionEnabled !== false,
      message_rate_cents:
        coachQ.data.message_rate_cents == null
          ? ""
          : String(coachQ.data.message_rate_cents),
      email: coachQ.data.email ?? "",
      website: coachQ.data.website ?? "",
      instagram: coachQ.data.socials?.instagram ?? "",
      twitter: coachQ.data.socials?.twitter ?? "",
      linkedin: coachQ.data.socials?.linkedin ?? "",
      tiktok: coachQ.data.socials?.tiktok ?? "",
      facebook: coachQ.data.socials?.facebook ?? "",
    })
  }, [isEdit, coachQ.data, reset])

  const existingAvatar = isEdit ? coachQ.data?.avatar_url || undefined : undefined
  const existingCover = isEdit ? coachQ.data?.cover_url || undefined : undefined
  const existingSlug = isEdit ? coachQ.data?.slug : ""

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)

    const payload: CoachWritePayload = {
      ...(isEdit && id ? { id } : {}),
      name: values.name,
      tagline: values.tagline || "",
      bio: values.bio || "",
      topics: values.topics,
      display_order: values.display_order,
      is_published: values.is_published,
      isPublicCoach: values.isPublicCoach,
      verified: values.verified,
      acceptsConversations: values.acceptsConversations,
      subscriptionEnabled: values.subscriptionEnabled,
      message_rate_cents:
        values.message_rate_cents === ""
          ? null
          : Number(values.message_rate_cents),
      email: values.email,
      website: values.website,
      socials: {
        instagram: values.instagram,
        twitter: values.twitter,
        linkedin: values.linkedin,
        tiktok: values.tiktok,
        facebook: values.facebook,
      },
    }
    if (avatarResult?.s3response) payload.avatar = avatarResult.s3response
    if (coverResult?.s3response) payload.cover = coverResult.s3response

    try {
      const res = await submittingMut.mutateAsync(payload)
      if (!res?.success) {
        setSubmitError(
          res?.message || `Failed to ${isEdit ? "update" : "create"} coach.`
        )
        return
      }
      navigate("/coaches")
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : `Failed to ${isEdit ? "update" : "create"} coach.`
      )
    }
  })

  const isLoadingPrefill = isEdit && coachQ.isLoading
  const uploading = isUploading > 0

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <a href="/coaches">
            <ChevronLeft className="mr-1 h-4 w-4" /> Back to creators
          </a>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? "Edit creator" : "Create creator"}</CardTitle>
          <CardDescription>
            {isEdit
              ? coachQ.data?.name || "Loading…"
              : "Add a creator record. The slug is generated from the name."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEdit && coachQ.isError && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load this creator. Try refreshing.</span>
            </div>
          )}

          {isLoadingPrefill ? (
            <div className="grid h-32 place-items-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <form className="space-y-6" onSubmit={onSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="name">
                  Name<span className="text-destructive"> *</span>
                </Label>
                <Input
                  id="name"
                  aria-invalid={!!errors.name}
                  {...register("name")}
                />
                {errors.name && (
                  <p className="text-xs text-destructive">{errors.name.message}</p>
                )}
              </div>

              {isEdit && (
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug</Label>
                  <Input
                    id="slug"
                    value={existingSlug || ""}
                    readOnly
                    className="bg-muted/40"
                  />
                  <p className="text-xs text-muted-foreground">
                    Auto-generated from the name. Regenerated if you change the name.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="tagline">Tagline</Label>
                <Input
                  id="tagline"
                  placeholder="e.g. Neuroscience of Sleep & Peak Recovery"
                  maxLength={120}
                  aria-invalid={!!errors.tagline}
                  {...register("tagline")}
                />
                <p className="text-xs text-muted-foreground">
                  One-line headline shown under the creator's name on the
                  onboarding CoachesFollow rail and profile screen. Keep
                  it short — mobile truncates around 60 characters.
                </p>
                {errors.tagline && (
                  <p className="text-xs text-destructive">
                    {errors.tagline.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  rows={4}
                  aria-invalid={!!errors.bio}
                  {...register("bio")}
                />
                {errors.bio && (
                  <p className="text-xs text-destructive">{errors.bio.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Topics</Label>
                <Controller
                  control={control}
                  name="topics"
                  render={({ field }) => (
                    <TagsInput
                      value={field.value ?? []}
                      onChange={field.onChange}
                      placeholder="Add a topic and press Enter…"
                      disabled={submittingMut.isPending}
                    />
                  )}
                />
                <p className="text-xs text-muted-foreground">
                  Short tags shown on the coach card (e.g. "Career Strategy").
                </p>
              </div>

              <div className="space-y-2">
                <Label>Avatar</Label>
                <CoverImageDrop
                  initialPreview={existingAvatar}
                  result={avatarResult}
                  onResult={setAvatarResult}
                  onUploadingChange={(u) =>
                    setIsUploading((n) => Math.max(0, n + (u ? 1 : -1)))
                  }
                  disabled={submittingMut.isPending}
                />
              </div>

              <div className="space-y-2">
                <Label>Cover image</Label>
                <CoverImageDrop
                  initialPreview={existingCover}
                  result={coverResult}
                  onResult={setCoverResult}
                  onUploadingChange={(u) =>
                    setIsUploading((n) => Math.max(0, n + (u ? 1 : -1)))
                  }
                  disabled={submittingMut.isPending}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_order">Display order</Label>
                <Input
                  id="display_order"
                  type="number"
                  min={0}
                  className="max-w-xs"
                  {...register("display_order")}
                />
                {errors.display_order && (
                  <p className="text-xs text-destructive">
                    {errors.display_order.message}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label htmlFor="is_published" className="cursor-pointer">
                    Published
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Show this coach in the mobile directory.
                  </p>
                </div>
                <Controller
                  control={control}
                  name="is_published"
                  render={({ field }) => (
                    <Switch
                      id="is_published"
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                      disabled={submittingMut.isPending}
                    />
                  )}
                />
              </div>

              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label htmlFor="isPublicCoach" className="cursor-pointer">
                    List in directory
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Surface this coach in the mobile browse-coaches list
                    and the featured rail. Off for backfilled author
                    stubs whose only job is to render the name + avatar
                    on content cards.
                  </p>
                </div>
                <Controller
                  control={control}
                  name="isPublicCoach"
                  render={({ field }) => (
                    <Switch
                      id="isPublicCoach"
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                      disabled={submittingMut.isPending}
                    />
                  )}
                />
              </div>

              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label htmlFor="verified" className="cursor-pointer">
                    Verified ✓
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Adds the verified checkmark to the creator's cards in
                    the mobile app.
                  </p>
                </div>
                <Controller
                  control={control}
                  name="verified"
                  render={({ field }) => (
                    <Switch
                      id="verified"
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                      disabled={submittingMut.isPending}
                    />
                  )}
                />
              </div>

              {/* V-O / V-Q12: per-coach CTA hide toggles. Both default
                  ON — flip OFF to remove the corresponding button from
                  the coach's cards on mobile. Ask hiding is
                  independent of paid-DM pricing; Subscribe hiding is
                  independent of the app-wide COACH_SUBSCRIPTION_ENABLED
                  flag. Wrap in a single group with a clarifying header
                  so it's obvious these two live together. */}
              <div className="space-y-2">
                <div className="text-sm font-medium">Mobile CTAs</div>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <Label htmlFor="acceptsConversations" className="cursor-pointer">
                      Show "Ask" button
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      When off, hides the "Ask" CTA on this creator's cards.
                      Existing threads keep working.
                    </p>
                  </div>
                  <Controller
                    control={control}
                    name="acceptsConversations"
                    render={({ field }) => (
                      <Switch
                        id="acceptsConversations"
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                        disabled={submittingMut.isPending}
                      />
                    )}
                  />
                </div>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <Label htmlFor="subscriptionEnabled" className="cursor-pointer">
                      Show "Subscribe" button
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      When off, hides the "Subscribe" CTA on this creator's
                      cards. Independent of the app-wide subscription flag.
                    </p>
                  </div>
                  <Controller
                    control={control}
                    name="subscriptionEnabled"
                    render={({ field }) => (
                      <Switch
                        id="subscriptionEnabled"
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                        disabled={submittingMut.isPending}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message_rate_cents">Paid DM rate</Label>
                <select
                  id="message_rate_cents"
                  className="block w-full max-w-xs rounded-md border bg-background px-3 py-2 text-sm"
                  {...register("message_rate_cents")}
                >
                  {RATE_TIERS.map((t) => (
                    <option
                      key={String(t.value ?? "null")}
                      value={t.value === null ? "" : String(t.value)}
                    >
                      {t.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Price users pay to send this creator a direct message.
                  The creator opts in via their dashboard before paid DMs
                  are accepted.
                </p>
              </div>

              {/* V-L Creator Profile — public contact + socials. All
                  fields optional; blank = mobile hides the row. */}
              <div className="space-y-4 rounded-md border p-4">
                <div>
                  <h3 className="text-sm font-semibold">Public contact &amp; social</h3>
                  <p className="text-xs text-muted-foreground">
                    Shown on the creator's mobile profile. Leave blank to
                    hide. Socials: handle only (no @, no URL).
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="hello@coach.com"
                    aria-invalid={!!errors.email}
                    {...register("email")}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    placeholder="https://coach.com"
                    aria-invalid={!!errors.website}
                    {...register("website")}
                  />
                  {errors.website && (
                    <p className="text-xs text-destructive">
                      {errors.website.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="instagram">Instagram handle</Label>
                  <Input
                    id="instagram"
                    placeholder="coachmarcus"
                    {...register("instagram")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Opens instagram.com/&lt;handle&gt; on mobile.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twitter">Twitter / X handle</Label>
                  <Input
                    id="twitter"
                    placeholder="coachmarcus"
                    {...register("twitter")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Opens twitter.com/&lt;handle&gt; on mobile.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="linkedin">LinkedIn slug or URL</Label>
                  <Input
                    id="linkedin"
                    placeholder="marcus-flow or https://linkedin.com/in/..."
                    {...register("linkedin")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Slug (after /in/) or paste the full profile URL.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tiktok">TikTok handle</Label>
                  <Input
                    id="tiktok"
                    placeholder="coachmarcus"
                    {...register("tiktok")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Opens tiktok.com/@&lt;handle&gt; on mobile.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="facebook">Facebook handle or URL</Label>
                  <Input
                    id="facebook"
                    placeholder="coachmarcus or https://facebook.com/..."
                    {...register("facebook")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Page handle, numeric id, or paste the full profile
                    URL.
                  </p>
                </div>
              </div>

              {submitError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  disabled={uploading || submittingMut.isPending}
                >
                  {(submittingMut.isPending || uploading) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {uploading
                    ? "Uploading…"
                    : submittingMut.isPending
                      ? isEdit
                        ? "Saving…"
                        : "Creating…"
                      : isEdit
                        ? "Update coach"
                        : "Create coach"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/coaches")}
                  disabled={submittingMut.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
